import { WIDGET_PATH } from '@/features/widget-design-studio/studio-targets.types';

import { anchor } from './setting-anchors.types';

import type { SettingAnchor } from './setting-anchors.types';

/**
 * The Widget Design Studio's ten panes.
 *
 * The pane is the unit here, the way a tab is on `/connections`: each one is a
 * whole part of the widget with its own controls, so "where do I change the
 * greeting" and "where do I set the brand colour" are ten different questions.
 * Indexing `/widget` alone answered all ten with "Design Studio" — which is what
 * it did, because the two `/widget` anchors that existed before this were both
 * cards inside ONE pane.
 *
 * **Every label and every keyword bag here already existed**, written for the
 * Studio's own All-settings panel and translated into all six locales
 * (`studio.target.*.title`, `studio.search.*`). This is the first surface the
 * search reached at zero translation cost, and reusing the Studio's own words
 * rather than writing a second set is also what keeps the two searches saying
 * the same thing about the same pane.
 *
 * `surfaces:edit` throughout, matching the two `/widget` anchors that predate
 * this file.
 */
export const STUDIO_SETTING_ANCHORS = {
  'studio-launcher': anchor(
    'setting-studio-launcher',
    WIDGET_PATH,
    'studio.target.launcher.title',
    'surfaces',
    'edit',
    {
      keywordsKey: 'studio.search.launcher',
      pane: 'launcher',
    },
  ),
  'studio-panel': anchor(
    'setting-studio-panel',
    WIDGET_PATH,
    'studio.target.panel.title',
    'surfaces',
    'edit',
    {
      keywordsKey: 'studio.search.panel',
      pane: 'panel',
      // Note the two OTHER anchors on this pane: widget branding and
      // anonymous access are cards INSIDE `GlobalSettingsPane`, so all three
      // results open the same pane and land on different things in it.
    },
  ),
  'studio-header': anchor(
    'setting-studio-header',
    WIDGET_PATH,
    'studio.target.header.title',
    'surfaces',
    'edit',
    {
      keywordsKey: 'studio.search.header',
      pane: 'header',
    },
  ),
  'studio-welcome': anchor(
    'setting-studio-welcome',
    WIDGET_PATH,
    'studio.target.welcome.title',
    'surfaces',
    'edit',
    {
      keywordsKey: 'studio.search.welcome',
      pane: 'welcome',
    },
  ),
  'studio-messages': anchor(
    'setting-studio-messages',
    WIDGET_PATH,
    'studio.target.messages.title',
    'surfaces',
    'edit',
    {
      keywordsKey: 'studio.search.messages',
      pane: 'messages',
    },
  ),
  'studio-composer': anchor(
    'setting-studio-composer',
    WIDGET_PATH,
    'studio.target.composer.title',
    'surfaces',
    'edit',
    {
      keywordsKey: 'studio.search.composer',
      pane: 'composer',
    },
  ),
  'studio-disclosure': anchor(
    'setting-studio-disclosure',
    WIDGET_PATH,
    'studio.target.disclosure.title',
    'surfaces',
    'edit',
    {
      keywordsKey: 'studio.search.disclosure',
      pane: 'disclosure',
    },
  ),
  'studio-components': anchor(
    'setting-studio-components',
    WIDGET_PATH,
    'studio.target.components.title',
    'surfaces',
    'edit',
    {
      keywordsKey: 'studio.search.componentStyling',
      pane: 'components',
      // The one advanced-only pane (`studio-targets.ts` — `advancedOnly`),
      // so Simple does not offer it and a link to it falls back.
      requiresAdvanced: true,
    },
  ),
  'studio-brand': anchor(
    'setting-studio-brand',
    WIDGET_PATH,
    'studio.target.brand.title',
    'surfaces',
    'edit',
    {
      keywordsKey: 'studio.search.brand',
      pane: 'brand',
    },
  ),
  'studio-language': anchor(
    'setting-studio-language',
    WIDGET_PATH,
    'studio.target.language.title',
    'surfaces',
    'edit',
    {
      keywordsKey: 'studio.search.language',
      pane: 'language',
    },
  ),
} as const satisfies Record<string, SettingAnchor>;
